<script setup lang="ts">
import { faSteeringWheel } from '@fortawesome/pro-regular-svg-icons';
import type Bus from '@itinerary/trip/bus/Bus';
import type BusPackage from '@itinerary/trip/bus/BusPackage';
import { type ServiceInfoDto } from '@clients/BusApi';
import { SeatStatus } from '@itinerary/trip/bus/types';

const { tripPackage } = defineProps<{
    tripPackage: BusPackage;
}>();
const bus_ = ref<Bus>();
const seatInfo = ref<ServiceInfoDto>();
const loaded = ref(false);
onMounted(async () => {
    bus_.value = tripPackage.firstTrip as Bus;
    try {
        seatInfo.value = await bus_.value.loadSeats();
    } catch (e) {
        reportException(e);
    }
    loaded.value = true;
});
</script>

<template>
    <div class="info-form" data-id="seat-select">
        <div class="box">
            <mr-loading v-if="!loaded" :scale="0.8" />
            <div id="seats-container" class="seats-container">
                <div v-if="seatInfo" class="transport-seats">
                    <div
                        v-for="(row, i) in seatInfo.seats || []"
                        :key="i"
                        class="seats-row"
                        :style="{
                            width: `${100 / (seatInfo.seats?.length || 0)}%`,
                        }"
                    >
                        <template v-for="(seat, j) in row" :key="`${j}.${i}`">
                            <div
                                class="seat"
                                :class="{
                                    'seat-occupied':
                                        seat.status ===
                                        SeatStatus.NotReservable,
                                    'seat-selected': seat.selected,
                                    'seat-noseat':
                                        seat.status === SeatStatus.NotAvailable,
                                    'seat-male':
                                        seat.status === SeatStatus.Male,
                                    'seat-female':
                                        seat.status === SeatStatus.Female,
                                    wheel: seat.status === SeatStatus.Wheel,
                                }"
                                data-set="0"
                            >
                                <mr-icon
                                    v-if="seat.status === SeatStatus.Wheel"
                                    class="wheel-icon"
                                    :icon="faSteeringWheel"
                                />
                            </div>
                            <div
                                v-if="j === (seatInfo.spacePlace || 0) - 1"
                                :style="{
                                    width: `${
                                        100 / ((seatInfo.columns || 0) + 1)
                                    }%`,
                                }"
                                class="seat-space"
                            />
                        </template>
                        <div class="clear" />
                    </div>
                    <div class="clear" />
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
$scale: 1.5;

.info-form {
    font-size: 12px * $scale;
    position: relative;
    border-radius: 5px * $scale;
    width: 260px * $scale;
    padding: 20px;
    @include mobile-only {
        transform: rotate(90deg) translate(-60px, 35%);
    }
}

.seats-container {
    display: flex;
    align-items: center;
    justify-content: center;
    .transport-seats {
        max-width: 220px * $scale;
    }
}

.seats-row {
    width: 20% * $scale;
    float: left;
    max-width: 240px * $scale;
}

.seat {
    user-select: none;
    width: 16px * $scale;
    height: 14px * $scale;
    margin: 4px * $scale 1px * $scale;
    background: usePalette();
    border-radius: 4px * $scale;
}

.seat-space {
    float: none;
    height: 6px * $scale;
}

.seat-noseat {
    visibility: hidden;
}

.seat-occupied,
.seat-male,
.seat-female {
    background-color: usePalette($p-cool-gray);
}

.wheel {
    background: none !important;
    .wheel-icon {
        transform: rotate(-90deg);
        @include mobile-only {
            transform: rotate(270deg);
        }
    }
}
</style>
